<template>
  <div class="notas">
    <div v-if="loading" class="loading">
      <i class="fas fa-spinner fa-spin"></i> Carregando indicadores de notas...
    </div>

    <template v-else>
      <el-row :gutter="10" class="u-mt-10">
        <el-col :xs="24" :sm="12" :md="6">
          <card-kpi
            label="Média geral"
            :value="dados.media_geral"
            :decimal="2"
            icon="fas fa-star-half-alt"
            :primary="dados.media_geral >= 6"
            :warn="dados.media_geral > 0 && dados.media_geral < 6"
          />
        </el-col>
        <el-col :xs="24" :sm="12" :md="6">
          <card-kpi
            label="% Alunos em recuperação"
            :value="dados.perc_recuperacao"
            :decimal="1"
            suffix="%"
            :hint="`${formatNumber(dados.total_em_recuperacao)} de ${formatNumber(dados.total_alunos_com_nota)}`"
            icon="fas fa-redo"
            :warn="dados.perc_recuperacao > 0 && dados.perc_recuperacao < 25"
            :danger="dados.perc_recuperacao >= 25"
          />
        </el-col>
        <el-col :xs="24" :sm="12" :md="6">
          <card-kpi
            label="Cobertura de lançamento"
            :value="dados.cobertura_lancamento.perc"
            :decimal="1"
            suffix="%"
            :hint="`${formatNumber(dados.cobertura_lancamento.lancado)} / ${formatNumber(dados.cobertura_lancamento.esperado)} células`"
            icon="fas fa-clipboard-check"
            :warn="dados.cobertura_lancamento.perc > 0 && dados.cobertura_lancamento.perc < 70"
          />
        </el-col>
        <el-col :xs="24" :sm="12" :md="6">
          <card-kpi
            label="Alunos com média final"
            :value="dados.total_com_media"
            icon="fas fa-user-check"
          />
        </el-col>
      </el-row>

      <el-row :gutter="10" class="u-mt-15">
        <el-col :xs="24" :sm="24" :md="12">
          <c-panel title="Distribuição de médias finais" titleIcon="fas fa-chart-bar">
            <div class="chart-box chart-box--md">
              <bar-chart
                v-if="totalDistribuicao > 0"
                class="chart-canvas"
                :chart-data="chartDistribuicao"
              />
              <div v-else class="empty">
                <el-empty description="Sem médias finais lançadas no período" :image-size="80" />
              </div>
            </div>
          </c-panel>
        </el-col>
        <el-col :xs="24" :sm="24" :md="12">
          <c-panel
            :title="`Média por disciplina (Top ${dados.medias_por_disciplina.length})`"
            titleIcon="fas fa-book"
          >
            <div class="chart-box chart-box--md">
              <bar-chart
                v-if="chartDisciplinas.labels.length > 0"
                class="chart-canvas"
                :chart-data="chartDisciplinas"
              />
              <div v-else class="empty">
                <el-empty description="Sem notas lançadas por disciplina" :image-size="80" />
              </div>
            </div>
          </c-panel>
        </el-col>
      </el-row>

      <el-row :gutter="10" class="u-mt-15">
        <el-col :xs="24" :sm="24" :md="12">
          <c-panel
            :title="`Média por turma (Top ${dados.medias_por_turma.length})`"
            titleIcon="fas fa-users"
          >
            <div class="chart-box chart-box--md">
              <bar-chart
                v-if="chartTurmas.labels.length > 0"
                class="chart-canvas"
                :chart-data="chartTurmas"
              />
              <div v-else class="empty">
                <el-empty description="Sem médias finais por turma" :image-size="80" />
              </div>
            </div>
          </c-panel>
        </el-col>
        <el-col :xs="24" :sm="24" :md="12">
          <c-panel
            :title="`Cobertura de lançamento por turma (Top ${dados.cobertura_lancamento.por_turma.length} mais críticas)`"
            titleIcon="fas fa-clipboard-list"
          >
            <div class="chart-box chart-box--md">
              <bar-chart
                v-if="chartCoberturaTurma.labels.length > 0"
                class="chart-canvas"
                :chart-data="chartCoberturaTurma"
              />
              <div v-else class="empty">
                <el-empty description="Sem dados de cobertura" :image-size="80" />
              </div>
            </div>
          </c-panel>
        </el-col>
      </el-row>

      <div class="atualizado-em" v-if="dados.atualizado_em">
        <i class="fas fa-clock"></i>
        Atualizado em {{ formatDateTime(dados.atualizado_em) }}
        <span class="cache-hint">(cache de 5 min)</span>
      </div>
    </template>
  </div>
</template>

<script>
export default {
  name: 'NotasTab',
  components: {
    CardKpi: () => import('../shared/CardKpi.vue'),
    cPanel: () => import('@/shared/c-panel/c-panel.vue'),
    BarChart: () => import('@/chart/BarChart')
  },
  props: {
    periodoId: { type: [Number, String], default: null },
    instituicaoId: { type: [Number, String], default: null },
    municipal: { type: Boolean, default: false }
  },
  data: () => ({
    loading: false,
    dados: {
      distribuicao_medias: [
        { faixa: '0-2.9', qtd: 0 },
        { faixa: '3-4.9', qtd: 0 },
        { faixa: '5-6.9', qtd: 0 },
        { faixa: '7-10', qtd: 0 }
      ],
      media_geral: 0,
      total_com_media: 0,
      perc_recuperacao: 0,
      total_em_recuperacao: 0,
      total_alunos_com_nota: 0,
      medias_por_disciplina: [],
      medias_por_turma: [],
      cobertura_lancamento: { perc: 0, esperado: 0, lancado: 0, por_turma: [] },
      atualizado_em: null
    }
  }),
  computed: {
    totalDistribuicao () {
      return (this.dados.distribuicao_medias || []).reduce((acc, it) => acc + Number(it.qtd || 0), 0)
    },
    chartDistribuicao () {
      const lista = this.dados.distribuicao_medias || []
      return {
        labels: lista.map(it => it.faixa),
        datasets: [{
          label: 'Qtd. alunos',
          data: lista.map(it => Number(it.qtd || 0)),
          backgroundColor: ['#d0021b', '#f5a623', '#4a90e2', '#50964d']
        }]
      }
    },
    chartDisciplinas () {
      const lista = this.dados.medias_por_disciplina || []
      return {
        labels: lista.map(it => this.truncate(it.disciplina, 30)),
        datasets: [{
          label: 'Média',
          data: lista.map(it => Number(it.media || 0)),
          backgroundColor: lista.map(it => {
            const m = Number(it.media || 0)
            if (m >= 7) return '#50964d'
            if (m >= 5) return '#4a90e2'
            if (m >= 3) return '#f5a623'
            return '#d0021b'
          })
        }]
      }
    },
    chartTurmas () {
      const lista = this.dados.medias_por_turma || []
      return {
        labels: lista.map(it => this.truncate(it.turma, 35)),
        datasets: [{
          label: 'Média final',
          data: lista.map(it => Number(it.media || 0)),
          backgroundColor: lista.map(it => {
            const m = Number(it.media || 0)
            if (m >= 7) return '#50964d'
            if (m >= 5) return '#4a90e2'
            if (m >= 3) return '#f5a623'
            return '#d0021b'
          })
        }]
      }
    },
    chartCoberturaTurma () {
      const lista = this.dados.cobertura_lancamento.por_turma || []
      return {
        labels: lista.map(it => this.truncate(it.turma, 35)),
        datasets: [{
          label: 'Cobertura (%)',
          data: lista.map(it => Number(it.perc || 0)),
          backgroundColor: lista.map(it => {
            const p = Number(it.perc || 0)
            if (p >= 75) return '#50964d'
            if (p >= 50) return '#f5a623'
            return '#d0021b'
          })
        }]
      }
    }
  },
  watch: {
    periodoId () { this.load() },
    instituicaoId () { this.load() },
    municipal () { this.load() }
  },
  mounted () {
    this.load()
  },
  methods: {
    load () {
      if (!this.periodoId) return
      if (!this.municipal && !this.instituicaoId) return

      this.loading = true
      const base = this.$store.state.config.baseUrlApi
      let url
      let payload = {}

      if (this.municipal) {
        url = `${base}/api/dashboard-diario/municipal/notas/${this.periodoId}`
        if (this.instituicaoId) payload.instituicao_id = this.instituicaoId
      } else {
        url = `${base}/api/dashboard-diario/notas/${this.instituicaoId}/${this.periodoId}`
      }

      this.$axios.post(url, payload, this.$store.getters.getHeaderAuth)
        .then(response => {
          this.dados = Object.assign({}, this.dados, response.data || {})
          this.loading = false
        })
        .catch(error => {
          this.loading = false
          this.$store.commit('throwException', error)
        })
    },
    formatNumber (v) {
      return Number(v || 0).toLocaleString('pt-BR')
    },
    truncate (str, len) {
      if (!str) return ''
      return str.length > len ? str.slice(0, len - 1) + '…' : str
    },
    formatDateTime (iso) {
      if (!iso) return ''
      try { return this.$moment(iso).format('DD/MM/YYYY HH:mm') } catch (e) { return iso }
    }
  }
}
</script>

<style scoped>
.chart-box { position: relative; width: 100%; overflow: hidden; height: 280px; }
.chart-canvas { display: block; width: 100% !important; height: 100% !important; }
.chart-box ::v-deep canvas { width: 100% !important; height: 100% !important; }
.empty {
  display: flex;
  height: 100%;
  align-items: center;
  justify-content: center;
}
.loading {
  padding: 40px 0;
  text-align: center;
  color: rgba(0, 0, 0, 0.45);
}
.loading i { margin-right: 6px; color: #346daa; }
.atualizado-em {
  font-size: 11px;
  color: rgba(0, 0, 0, 0.45);
  text-align: right;
  margin-top: 12px;
  padding-right: 6px;
}
.cache-hint { color: rgba(0, 0, 0, 0.30); margin-left: 4px; }
</style>
